import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { IconGripVertical, IconPlus } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import classes from './V2Form.module.css'

/**
 * Slide tabs as in the Ad Builder: "⋮⋮ Slide 1", "⋮⋮ Slide 2" and "+ Add Slide" (hidden at the
 * template's maximum) in one bordered strip, the active tab light pink, then the hint "Drag item
 * to rearrange slide sequence." Tabs reorder by drag or, when focused, with the keyboard (space to
 * lift, arrows to move) — @hello-pangea/dnd handles both.
 */
export function SlideTabs({
  items,
  selected,
  onSelect,
  onMove,
  onAdd,
  canAdd,
  hint,
  identity = (item, index) => item?.id || `slide-${index}`,
}) {
  const { t } = useTranslation()
  const hintText = hint === undefined ? t('editorV2.form.slides.reorderHint') : hint
  const onDragEnd = ({ source, destination }) => {
    if (!destination || destination.index === source.index) return
    onMove(source.index, destination.index)
  }

  return (
    <div>
      <div className={classes.slideTabs}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="slide-tabs" direction="horizontal">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className={classes.slideList} role="tablist" aria-label={t('editorV2.form.slides.slides')}>
                {items.map((item, index) => (
                  // The tab is a <button>; the library blocks drags that start on interactive
                  // elements unless told otherwise, which left the tabs impossible to reorder.
                  <Draggable
                    key={identity(item, index)}
                    draggableId={String(identity(item, index))}
                    index={index}
                    disableInteractiveElementBlocking
                  >
                    {(dragProvided, snapshot) => (
                      <button
                        type="button"
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        role="tab"
                        aria-selected={index === selected}
                        data-active={index === selected ? 'true' : 'false'}
                        data-dragging={snapshot.isDragging ? 'true' : undefined}
                        className={classes.slideTab}
                        onClick={() => onSelect(index)}
                      >
                        <IconGripVertical size={16} stroke={1.8} aria-hidden="true" />
                        <span>{t('editorV2.form.slides.slideNumber', { number: index + 1 })}</span>
                      </button>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {canAdd && (
          <button type="button" className={classes.newSlide} onClick={onAdd}>
            <IconPlus size={14} stroke={1.8} aria-hidden="true" />
            {t('editorV2.form.slides.addSlide')}
          </button>
        )}
      </div>
      {items.length > 1 && hintText && <p className={classes.hint}>{hintText}</p>}
    </div>
  )
}
